import React from "react";

/** @typedef {'asc'|'desc'} SortDir */

/**
 * Compact carets (#637381) — active direction gets stronger emphasis.
 * @param {{ direction: SortDir | null; active: boolean }} props
 */
function SortCarets({ direction, active }) {
  const upStrong = active && direction === "asc";
  const downStrong = active && direction === "desc";
  return (
    <span className="pos-dashboard-chart-table__sort-carets" aria-hidden="true">
      <svg
        className={`pos-dashboard-chart-table__sort-caret pos-dashboard-chart-table__sort-caret--up ${upStrong ? "is-strong" : ""}`}
        width="10"
        height="7"
        viewBox="0 0 10 7"
        fill="none"
      >
        <path d="M1 5.25L5 1l4 4.25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg
        className={`pos-dashboard-chart-table__sort-caret pos-dashboard-chart-table__sort-caret--down ${downStrong ? "is-strong" : ""}`}
        width="10"
        height="7"
        viewBox="0 0 10 7"
        fill="none"
      >
        <path d="M9 1.75L5 6 1 1.75" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/**
 * Sortable `<th>` content: label + hover carets; `aria-sort` when active.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.ariaSortLabel — e.g. "Sort by Date"
 * @param {boolean} props.sorted — this column is active
 * @param {SortDir | null} props.direction
 * @param {() => void} props.onSort
 * @param {string} [props.className] — extra BEM modifiers (e.g. pos-dashboard-chart-table__th--first)
 * @param {string} [props.baseClassName] — header cell base (default `pos-dashboard-chart-table__th`; use `pos-segment-table__th` for segment card)
 * @param {string} [props.sortedClassName] — appended when sorted (default `pos-dashboard-chart-table__th--sorted`)
 */
export function DashboardChartTableSortHeader({
  children,
  ariaSortLabel,
  sorted,
  direction,
  onSort,
  className = "",
  baseClassName = "pos-dashboard-chart-table__th",
  sortedClassName = "pos-dashboard-chart-table__th--sorted",
}) {
  const ariaSort = sorted && direction === "asc" ? "ascending" : sorted && direction === "desc" ? "descending" : "none";

  return (
    <div className={`${baseClassName}${sorted ? ` ${sortedClassName}` : ""}${className ? ` ${className}` : ""}`} role="columnheader" aria-sort={ariaSort}>
      <button type="button" className="pos-dashboard-chart-table__sort-trigger" onClick={onSort} aria-label={ariaSortLabel}>
        <span className="pos-dashboard-chart-table__sort-label">{children}</span>
        <SortCarets direction={sorted ? direction : null} active={sorted} />
      </button>
    </div>
  );
}
